import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AudioRecordingCoordinator } from "@/store/audio-capture/coordinator";
import { uuidModule } from "@/modules/uuid";
import { clientEnvModule } from "@/modules/client-env";
import { useAppStore } from "@/store";
import { createRunController, RunController } from "../utils";

type RunStatus = "IDLE" | "RECORDING" | "PAUSED" | "STOPPED";

export const useAudioAudioRecording = () => {
  const { store } = useAppStore();
  const coordinator = useMemo(
    () => new AudioRecordingCoordinator(store.memDb.audioRecordings),
    [store.memDb.audioRecordings]
  );

  const [status, setStatus] = useState<RunStatus>("IDLE");
  const [recordingId, setRunId] = useState<string | null>(null);
  const [noteId, setNoteId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<RunController | null>(null);
  const engine = clientEnvModule.isDesktop() ? "DESKTOP" : "WEB";
  const recordingStartRef = useRef<number | null>(null);
  const baseCapturedRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const isMountedRef = useRef(true);

  const setLevelIfMounted = useCallback((value: number) => {
    if (!isMountedRef.current) {
      return;
    }

    if (!Number.isFinite(value) || value < 0) {
      setLevel(0);
      return;
    }

    setLevel(value);
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (timerRef.current !== null) {
        clearInterval(timerRef.current);
      }
      controllerRef.current?.dispose();
    };
  }, []);

  const updateElapsed = () => {
    const start = recordingStartRef.current;
    const base = baseCapturedRef.current;
    if (start) {
      const current = base + (Date.now() - start);
      setElapsedMs(current);
    } else {
      setElapsedMs(base);
    }
  };

  const startTimer = () => {
    if (timerRef.current !== null) clearInterval(timerRef.current);
    timerRef.current = window.setInterval(updateElapsed, 500);
  };

  const stopTimer = () => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    updateElapsed();
  };

  const start = async ({
    noteId: runNoteId,
    capturedAudioId: providedCapturedAudioId,
    recordingId: providedRunId,
  }: {
    noteId?: string;
    capturedAudioId?: string;
    recordingId?: string;
  }) => {
    if (status !== "IDLE") return;
    try {
      const generatedRunId = providedRunId ?? uuidModule.generate();
      const capturedAudioId = providedCapturedAudioId || uuidModule.generate();
      setNoteId(runNoteId);

      if (!providedRunId) {
        const snapshot = await coordinator.startRecording({
          recordingId: generatedRunId,
          noteId: runNoteId,
          capturedAudioId,
          engine,
        });

        if (!snapshot) throw new Error("Failed to create run");
      }

      setRunId(generatedRunId);
      setStatus("RECORDING");
      setLevelIfMounted(0);
      recordingStartRef.current = Date.now();
      baseCapturedRef.current = 0;
      startTimer();
      setElapsedMs(0);

      const controller = createRunController({
        recordingId: generatedRunId,
        noteId: runNoteId,
        onChunk: async blob => {
          await coordinator.appendAudioChunk({ recordingId: generatedRunId, blob });
        },
        onError: err => setError(err.message),
        onStop: () => {
          stopTimer();
          setLevelIfMounted(0);
        },
        onLevel: nextLevel => {
          setLevelIfMounted(nextLevel);
        },
      });

      controllerRef.current = controller;
      await controller.start();
      await coordinator.markRecording({ recordingId: generatedRunId });
      setLevelIfMounted(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start recording");
      setStatus("IDLE");
      controllerRef.current?.dispose();
      controllerRef.current = null;
      setLevelIfMounted(0);
    }
  };

  const pause = async () => {
    if (status !== "RECORDING" || !recordingId) return;
    try {
      await controllerRef.current?.pause();
      const start = recordingStartRef.current;
      if (start) {
        baseCapturedRef.current += Date.now() - start;
        recordingStartRef.current = null;
        stopTimer();
        await coordinator.markPaused({
          recordingId,
          progress: {
            totalCapturedMs: baseCapturedRef.current,
            pausedAccumulatedMs: baseCapturedRef.current,
          },
        });
      }
      setStatus("PAUSED");
      setLevelIfMounted(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to pause recording");
    }
  };

  const resume = async () => {
    if (status !== "PAUSED" || !recordingId) return;
    try {
      await controllerRef.current?.resume();
      recordingStartRef.current = Date.now();
      startTimer();
      await coordinator.markResumed({
        recordingId,
        progress: { totalCapturedMs: baseCapturedRef.current },
      });
      setStatus("RECORDING");
      setLevelIfMounted(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to resume recording");
    }
  };

  const stop = async () => {
    if ((status !== "RECORDING" && status !== "PAUSED") || !recordingId) return;
    try {
      if (status === "RECORDING" && recordingStartRef.current) {
        baseCapturedRef.current += Date.now() - recordingStartRef.current;
        recordingStartRef.current = null;
      }
      stopTimer();
      await coordinator.markStopping({ recordingId });
      await controllerRef.current?.stop();
      controllerRef.current?.dispose();
      controllerRef.current = null;
      await coordinator.markStopped({
        recordingId,
        progress: {
          totalCapturedMs: baseCapturedRef.current,
          pausedAccumulatedMs: baseCapturedRef.current,
        },
      });
      setStatus("STOPPED");
      setLevelIfMounted(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to stop recording");
    }
  };

  const normalizedLevel = Number.isFinite(level) && level > 0 ? Math.min(level, 1) : 0;

  return {
    level: normalizedLevel,
    status,
    recordingId,
    noteId,
    error,
    start,
    pause,
    resume,
    stop,
    engine,
    elapsedMs,
  };
};

export default useAudioAudioRecording;
